import { Link2, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { useOAuthAuthorizationUrl } from '@/hooks/review-platforms/useOAuthAuthorizationUrl';
import { PLATFORM_CAPABILITIES, type ReviewPlatform } from '@/types/review-platform';

interface OAuthConnectFlowProps {
  platform: Extract<ReviewPlatform, 'GOOGLE' | 'FACEBOOK'>;
  locationId: string | null;
  /**
   * The frontend route the OAuth callback should redirect to once the
   * provider redirects back to operations. Defaults to the current page.
   */
  returnTo?: string;
  label?: string;
  variant?: 'default' | 'outline';
}

/**
 * "Connect with Google / Facebook" button. Fetches the provider authorization
 * URL from the backend, then redirects the browser. When the callback returns,
 * the parent page re-renders with `?status=select-business&oauthTokenId=...`
 * in the query string and the `BusinessLocationPicker` finalizes the flow.
 */
export function OAuthConnectFlow({
  platform,
  locationId,
  returnTo,
  label,
  variant = 'default',
}: OAuthConnectFlowProps) {
  const cap = PLATFORM_CAPABILITIES[platform];
  const slug = cap.oauthSlug;
  const mutation = useOAuthAuthorizationUrl();

  if (!slug) {
    // Shouldn't happen — only OAuth platforms get this component.
    return null;
  }

  function handleConnect() {
    if (!slug) return;
    const finalReturnTo = returnTo ?? `${window.location.origin}${window.location.pathname}`;
    mutation.mutate(
      {
        platformSlug: slug,
        ...(locationId ? { locationId } : {}),
        returnTo: finalReturnTo,
      },
      {
        onSuccess: ({ authorizationUrl }) => {
          window.location.href = authorizationUrl;
        },
      },
    );
  }

  return (
    <Button
      type="button"
      size="sm"
      variant={variant}
      onClick={handleConnect}
      disabled={mutation.isPending}
    >
      {mutation.isPending ? (
        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
      ) : (
        <Link2 className="mr-2 h-3.5 w-3.5" />
      )}
      {label ?? 'Connect'}
    </Button>
  );
}
